import React, { useState } from 'react';
import LZString from 'lz-string';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './po-summary.css';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

// Utility functions
const formatNumber = (num) => {
    return num ? num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "0";
};

const formatCurrency = (value) => {
    return `${formatNumber(Math.floor(value))}`;
};

// Define color palette
const colorPalette = [
    '#2E86AB', '#A23B72', '#F18F01', '#C73E1D', '#3B1F2B',
    '#44CF6C', '#4B3F72', '#F49F0A', '#2E5266', '#70A288'
];

const POSummary2 = ({ inventory, coreProducts }) => {
    const [showPercentages, setShowPercentages] = useState(true);
    const [showRelative, setShowRelative] = useState(false);

    const calculateMonthlySales = (orders) => {
        const monthlySales = {};
        
        if (Array.isArray(orders)) {
            const productTagsMap = {};
            inventory.forEach(product => {
                if (Array.isArray(product.tags)) {
                    const productId = product.product_id?.toString();
                    if (productId) {
                        productTagsMap[productId] = product.tags.filter(tag => tag.startsWith('stock-'));
                    }
                }
            });

            orders.forEach(order => {
                if (!order || !order.line_items) return;

                const orderDate = new Date(order.created_at);
                const monthKey = `${orderDate.getFullYear()}-${String(orderDate.getMonth() + 1).padStart(2, '0')}`;
                
                if (!monthlySales[monthKey]) {
                    monthlySales[monthKey] = {};
                }

                order.line_items.forEach(item => {
                    if (!item || !item.product_id) return;

                    const productId = item.product_id.toString();
                    const tags = productTagsMap[productId];
                    
                    if (tags && tags.length > 0) {
                        const quantity = parseInt(item.quantity) || 0;
                        const price = parseFloat(item.price) || 0;
                        const value = quantity * price;

                        tags.forEach(tag => {
                            if (!monthlySales[monthKey][tag]) {
                                monthlySales[monthKey][tag] = 0;
                            }
                            monthlySales[monthKey][tag] += value;
                        });
                    }
                });
            });
        }

        return monthlySales;
    };

    const monthlySalesData = (() => {
        try {
            const compressedOrders = sessionStorage.getItem('rawOrdersData');
            if (!compressedOrders) return {};

            const orders = JSON.parse(LZString.decompress(compressedOrders));
            if (!Array.isArray(orders)) return {};

            return calculateMonthlySales(orders);
        } catch (error) {
            console.error('Error processing orders for value chart:', error);
            return {};
        }
    })();

    // Calculate monthly totals and category totals
    const monthlyTotals = {};
    Object.keys(monthlySalesData).forEach(month => {
        monthlyTotals[month] = Object.values(monthlySalesData[month]).reduce((sum, val) => sum + val, 0);
    });

    const categoryTotals = {};
    Object.keys(monthlySalesData).forEach(month => {
        Object.entries(monthlySalesData[month]).forEach(([tag, value]) => {
            if (!categoryTotals[tag]) categoryTotals[tag] = 0;
            categoryTotals[tag] += value;
        });
    });

    const chartData = {
        labels: Object.keys(monthlySalesData).sort().map(month => {
            const [year, monthNum] = month.split('-');
            return `${monthNum}/${year}`;
        }),
        datasets: Object.keys(categoryTotals)
            // Sort alphabetically by tag name
            .sort((a, b) => a.localeCompare(b))
            .map((tag, index) => ({
                label: tag.replace('stock-', ''),
                data: Object.keys(monthlySalesData)
                    .sort()
                    .map(month => monthlySalesData[month][tag] || 0),
                backgroundColor: colorPalette[index % colorPalette.length],
                borderColor: colorPalette[index % colorPalette.length],
                borderWidth: 1
            }))
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                stacked: true,
                title: {
                    display: true,
                    text: 'Month',
                    font: { size: 14, weight: 'bold' }
                }
            },
            y: {
                stacked: true,
                beginAtZero: true,
                title: {
                    display: true,
                    text: showRelative ? 'Percentage (%)' : 'Sales Value (RON)',
                    font: { size: 14, weight: 'bold' }
                },
                min: 0,
                max: showRelative ? 100 : undefined,
                ticks: {
                    callback: function(value) {
                        return showRelative ? `${value}%` : formatNumber(value);
                    }
                }
            }
        },
        plugins: {
            title: {
                display: true,
                text: 'Monthly Sales Value RON',
                font: { size: 18, weight: 'bold' },
                padding: 20
            },
            legend: {
                display: true,
                position: 'right',
                reverse: false,
                labels: {
                    boxWidth: 15,
                    padding: 15,
                    font: { size: 12 }
                }
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: function(context) {
                        const monthKey = Object.keys(monthlySalesData).sort()[context.dataIndex];
                        const originalValue = showRelative ? 
                            context.dataset.originalData[context.dataIndex] : 
                            context.raw;
                        const monthTotal = monthlyTotals[monthKey];
                        const percentage = ((originalValue / monthTotal) * 100).toFixed(1);
                        
                        if (showRelative) {
                            return `${context.dataset.label}: ${percentage}%`;
                        }
                        return `${context.dataset.label}: ${formatNumber(originalValue)} (${percentage}%)`;
                    }
                }
            },
            datalabels: {
                display: true,
                formatter: function(value, context) {
                    const monthKey = Object.keys(monthlySalesData).sort()[context.dataIndex];
                    const originalValue = showRelative ? 
                        context.dataset.originalData[context.dataIndex] : 
                        value;
                    const monthTotal = monthlyTotals[monthKey];
                    const percentage = ((originalValue / monthTotal) * 100).toFixed(1);
                    
                    return showPercentages ? `${percentage}%` : formatNumber(originalValue);
                },
                color: 'white',
                font: { weight: 'bold' },
                anchor: 'center',
                align: 'center'
            }
        }
    };

    // Update the data transformation for relative view
    if (showRelative) {
        // Store original data for percentage calculations
        chartData.datasets = chartData.datasets.map(dataset => ({
            ...dataset,
            originalData: [...dataset.data], // Store original values
            data: dataset.data.map((value, index) => {
                const monthKey = Object.keys(monthlySalesData).sort()[index];
                const total = monthlyTotals[monthKey];
                return total > 0 ? (value / total) * 100 : 0;
            })
        }));
    }

    return (
        <div className="monthly-sales-chart" style={{ 
            marginTop: '2rem', 
            height: '600px',
            padding: '20px',
            overflow: 'hidden'
        }}>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '1rem'
            }}>
                <h3>Monthly Sales by Value</h3>
                <div className="display-toggle" style={{ display: 'flex', gap: '20px' }}>
                    <div>
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={showPercentages}
                                onChange={() => setShowPercentages(!showPercentages)}
                            />
                            <span className="slider round"></span>
                        </label>
                        <span style={{ marginLeft: '8px' }}>
                            Show {showPercentages ? 'Percentages' : 'Values'}
                        </span>
                    </div>
                    <div>
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={showRelative}
                                onChange={() => setShowRelative(!showRelative)}
                            />
                            <span className="slider round"></span>
                        </label>
                        <span style={{ marginLeft: '8px' }}>
                            {showRelative ? 'Relative' : 'Absolute'} View
                        </span>
                    </div>
                </div>
            </div>
            <div style={{ height: 'calc(100% - 60px)' }}>
                <Bar data={chartData} options={chartOptions} />
            </div>
        </div>
    );
};

export default POSummary2; 